import React, { useState, useEffect, useRef } from 'react';
import { generateQuestionGenerationHTML } from '../utils/templateLoader';
import QuestionReviewEditor from './QuestionReviewEditor';
import DeploymentModal from './DeploymentModal';
import Header from './Header';
import githubService from '../utils/githubService';

const QuestionGeneratorWindow = () => {
  const [sheetUrl, setSheetUrl] = useState('');
  const [passages, setPassages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPassage, setSelectedPassage] = useState(null);
  const [generatingQuestions, setGeneratingQuestions] = useState(false);
  const [questionSettings, setQuestionSettings] = useState({
    schoolLevel: '',
    grade: ''
  });
  const [reviewMode, setReviewMode] = useState(false);
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [originalQuestions, setOriginalQuestions] = useState([]);
  const [currentMetadata, setCurrentMetadata] = useState(null);
  const [processingHTML, setProcessingHTML] = useState(false);
  const abortController = useRef(null);

  // DeploymentModal 관련 state
  const [showDeploymentModal, setShowDeploymentModal] = useState(false);
  const [finalHtmlContent, setFinalHtmlContent] = useState('');
  const [finalFileName, setFinalFileName] = useState('');

  // 사용자 설정 로드
  useEffect(() => {
    const loadUserSettings = () => {
      const savedSettings = localStorage.getItem('user_default_settings');
      if (savedSettings) {
        try {
          const parsedSettings = JSON.parse(savedSettings);
          if (parsedSettings.defaultSheetUrl) {
            setSheetUrl(parsedSettings.defaultSheetUrl);
            console.log('✅ 사용자 기본 구글 시트 URL 로드됨:', parsedSettings.defaultSheetUrl);
          }
        } catch (error) {
          console.error('❌ 사용자 설정 로드 실패:', error);
        }
      }
    };

    loadUserSettings();
  }, []);


  // 지문 목록 가져오기
  const fetchPassages = async () => {
    if (!sheetUrl) {
      setError('구글 시트 URL을 입력해주세요.');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      // 구글시트 ID 추출
      const sheetIdMatch = sheetUrl.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
      if (!sheetIdMatch) {
        throw new Error('올바른 구글시트 URL이 아닙니다.');
      }
      const sheetId = sheetIdMatch[1];

      // Google Apps Script로 지문 목록 조회  
      const webAppUrl = process.env.REACT_APP_APPSCRIPT_URL;
      console.log(webAppUrl);
      const url = `${webAppUrl}?sheetId=${sheetId}&menu=passage_list`;
      
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`HTTP 오류: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        console.log('🔍 Apps Script 응답 전체:', result);
        console.log('📊 전체 지문 수:', result.passages?.length);
        console.log('📝 첫 번째 지문 전체 데이터:', result.passages?.[0]);
        console.log('📚 첫 번째 지문의 passage 필드:', result.passages?.[0]?.passage ? '존재함 (길이: ' + result.passages[0].passage.length + ')' : '없음');
        console.log('📚 첫 번째 지문의 passageContent 필드:', result.passages?.[0]?.passageContent ? '존재함 (길이: ' + result.passages[0].passageContent.length + ')' : '없음');
        
        // '지문생성시스템'으로 생성된 지문만 필터링
        const filteredPassages = (result.passages || []).filter(passage => 
          passage.creator === '지문생성시스템'
        );
        
        console.log('🎯 필터링된 지문 수:', filteredPassages.length);
        console.log('📖 필터링된 첫 번째 지문:', filteredPassages[0]);
        console.log('📄 필터링된 지문의 passage 필드:', filteredPassages[0]?.passage ? '존재함 (길이: ' + filteredPassages[0].passage.length + ')' : '없음');
        
        setPassages(filteredPassages);
        if (filteredPassages.length === 0) {
          setError('저장된 지문이 없습니다. 먼저 지문을 생성해주세요.');
        }
      } else {
        throw new Error(result.error || '지문 목록 조회에 실패했습니다.');
      }
    } catch (err) {
      console.error('지문 목록 조회 오류:', err);
      setError(err.message);
      setPassages([]);
    } finally {
      setLoading(false);
    }
  };

  // 학교급 매핑 함수
  const mapSchoolLevel = (level) => {
    const levelMap = {
      '초등': 'elementary',
      '중등': 'middle', 
      '고등': 'high',
      'elementary': 'elementary',
      'middle': 'middle',
      'high': 'high',
      'advanced': 'advanced'
    };
    return levelMap[level] || '';
  };

  // 평균 예상 정답률 계산
  const calculateAvgAccuracy = (questions) => {
    if (!questions || questions.length === 0) return '';
    
    const accuracies = questions
      .map(q => q.expectedAccuracy || '')
      .filter(acc => acc)
      .map(acc => {
        const match = acc.match(/(\d+)/);
        return match ? parseInt(match[1]) : null;
      })
      .filter(num => num !== null);
    
    if (accuracies.length === 0) return '';
    
    const avg = Math.round(accuracies.reduce((sum, acc) => sum + acc, 0) / accuracies.length);
    return `${avg}%`;
  };

  // 문항생성 로그 저장
  const saveQuestionGenerationLog = async (logData) => {
    const sheetIdMatch = sheetUrl.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
    if (!sheetIdMatch) return;
    
    const sheetId = sheetIdMatch[1];
    const webAppUrl = process.env.REACT_APP_APPSCRIPT_URL;
    console.log('📊 문항생성 로그 저장 URL:', webAppUrl);
    
    const formData = new URLSearchParams({
      sheetId: sheetId,
      menu: 'question_generation_log',
      timestamp: logData.timestamp,
      passageId: logData.passageId,
      passageTitle: logData.passageTitle,
      schoolLevel: logData.schoolLevel,
      grade: logData.grade,
      passageLength: logData.passageLength,
      questionCount: logData.questionCount,
      difficultyInfo: logData.difficultyInfo,
      avgExpectedAccuracy: logData.avgExpectedAccuracy,
      generationTime: logData.generationTime,
      fileName: logData.fileName,
      gptModel: 'gpt-4o-mini'
    });
    
    // 새로운 필드들 추가
    if (logData.originalQuestions) {
      formData.append('originalQuestions', logData.originalQuestions);
    }
    if (logData.finalQuestions) {
      formData.append('finalQuestions', logData.finalQuestions);
    }
    if (logData.originalExplanations) {
      formData.append('originalExplanations', logData.originalExplanations);
    }
    if (logData.finalExplanations) {
      formData.append('finalExplanations', logData.finalExplanations);
    }
    if (logData.wasModified !== undefined) {
      formData.append('wasModified', logData.wasModified);
    }
    console.log('📊 문항생성 로그 저장 데이터:', Array.from(formData.entries()));
    const response = await fetch(webAppUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData
    });
    
    if (!response.ok) {
      throw new Error(`로그 저장 실패: ${response.status}`);
    }
    
    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || '로그 저장 실패');
    }
    
    console.log('📊 문항생성 로그 저장 완료:', logData.passageId);
  };


  // 지문 선택 (지문 전문이 이미 포함되어 있음)
  const selectPassage = (passage) => {
    console.log('선택된 지문:', passage);
    console.log('원본 schoolLevel:', passage.schoolLevel);
    console.log('매핑된 schoolLevel:', mapSchoolLevel(passage.schoolLevel));
    
    setSelectedPassage(passage);
    setQuestionSettings({
      schoolLevel: mapSchoolLevel(passage.schoolLevel),
      grade: passage.grade || ''
    });
  };

  // 문항 생성
  const generateQuestions = async () => {
    if (!selectedPassage || !questionSettings.schoolLevel || !questionSettings.grade) {
      alert('지문과 학교급, 학년을 선택해주세요.');
      return;
    }

    setGeneratingQuestions(true);
    abortController.current = new AbortController();
    const startTime = new Date();

    try {
      const response = await fetch('/api/generate-questions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          passage: selectedPassage.passage,
          schoolLevel: questionSettings.schoolLevel,
          grade: questionSettings.grade,
          passageSheetUrl: sheetUrl
        }),
        signal: abortController.current.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP 오류: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.questions) {
        // GPT 응답의 데이터 구조를 QuestionReviewEditor에 맞게 변환
        const convertedQuestions = result.questions.map(q => {
          const choices = q.options || q.choices || ['', '', '', ''];
          let correctIndex = q.correctIndex;
          let answer = q.answer;
          
          // correctIndex가 정의되어 있으면 해당 선택지를 answer로 설정
          if (correctIndex !== undefined && choices[correctIndex]) {
            answer = choices[correctIndex];
          } 
          // correctIndex가 없고 answer가 있으면, choices에서 찾아서 correctIndex 설정
          else if (answer && correctIndex === undefined) {
            correctIndex = choices.findIndex(choice => choice === answer);
            if (correctIndex === -1) correctIndex = 0; // 찾지 못하면 첫 번째를 정답으로
          }
          // 둘 다 없으면 기본값 설정
          else {
            correctIndex = 0;
            answer = choices[0] || '';
          }
          
          return {
            ...q,
            choices,
            correctIndex,
            answer
          };
        });
        
        console.log('변환된 문항 데이터:', convertedQuestions);
        
        // 검토 모드로 전환
        setOriginalQuestions([...convertedQuestions]);
        setGeneratedQuestions([...convertedQuestions]);
        setCurrentMetadata({
          ...result.metadata,
          generationTime: Math.round((new Date() - startTime) / 1000)
        });
        setReviewMode(true);
      } else {
        throw new Error(result.error || '문항 생성에 실패했습니다.');
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('문항 생성이 취소되었습니다.');
      } else {
        console.error('문항 생성 오류:', error);
        alert(`문항 생성 오류: ${error.message}`);
      }
    } finally {
      setGeneratingQuestions(false);
      abortController.current = null;
    }
  };

  // 문항 검토 완료 - DeploymentModal 열기
  const handleReviewConfirm = async (finalQuestions) => {
    setProcessingHTML(true);
    
    try {
      // 문항 데이터로 HTML 생성
      const htmlContent = await generateQuestionGenerationHTML(
        finalQuestions,
        selectedPassage.passage,
        currentMetadata,
        sheetUrl
      );

      const filename = `question_${currentMetadata.passageId}_${questionSettings.schoolLevel}_${questionSettings.grade}.html`;
      
      // HTML과 파일명 저장하고 배포 모달 열기
      setFinalHtmlContent(htmlContent);
      setFinalFileName(filename);
      setShowDeploymentModal(true);
      
      // 문항 검토 모달은 배포 모달 아래에 유지 (사용자가 배포를 취소할 수 있음)
      
      // 로그 저장 (기본)
      await saveQuestionGenerationLog({
        passageId: selectedPassage.passageId,
        passageTitle: selectedPassage.title,
        schoolLevel: questionSettings.schoolLevel,
        grade: questionSettings.grade,
        passageLength: selectedPassage.passageLength,
        questionCount: finalQuestions.length,
        difficultyInfo: currentMetadata?.difficultyInfo || '',
        avgExpectedAccuracy: calculateAvgAccuracy(finalQuestions),
        generationTime: `${currentMetadata.generationTime}초`,
        fileName: filename,
        timestamp: new Date().toLocaleString('ko-KR'),
        originalQuestions: JSON.stringify(originalQuestions),
        finalQuestions: JSON.stringify(finalQuestions),
        originalExplanations: originalQuestions.map(q => q.explanation || q.해설 || '').join('|||'),
        finalExplanations: finalQuestions.map(q => q.explanation || q.해설 || '').join('|||'),
        wasModified: JSON.stringify(originalQuestions) !== JSON.stringify(finalQuestions)
      });
      
      // 상태는 배포/다운로드 시에만 정리 (onDeploymentStart에서 처리)
      
    } catch (error) {
      console.error('HTML 생성 오류:', error);
      alert(`HTML 파일 생성 오류: ${error.message}`);
    } finally {
      setProcessingHTML(false);
    }
  };

  // 검토 취소
  const handleReviewCancel = () => {
    setReviewMode(false);
    setGeneratedQuestions([]);
    setOriginalQuestions([]);
    setCurrentMetadata(null);
  };

  // 검토 중 문항 업데이트
  const handleQuestionsUpdate = (updatedQuestions) => {
    setGeneratedQuestions(updatedQuestions);
  };

  // 생성 취소
  const cancelGeneration = () => {
    if (abortController.current) {
      abortController.current.abort();
    }
  };

  // 지문 미리보기
  const truncateText = (text, maxLength = 200) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleString('ko-KR');
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <Header />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        {/* 헤더 */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          
          {/* 구글 시트 URL 입력 */}
          <div className="flex gap-4 items-center">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                구글 시트 URL
              </label>
              <input
                type="url"
                value={sheetUrl}
                onChange={(e) => setSheetUrl(e.target.value)}
                placeholder="https://docs.google.com/spreadsheets/d/..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading}
              />
              <p className="text-xs text-gray-500 mt-1">
                지문마스터와 학습로그가 저장된 구글시트 URL을 입력하세요
              </p>
            </div>
            <button
              onClick={fetchPassages}
              disabled={loading || !sheetUrl}
              className="px-8 py-3 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium"
            >
              {loading ? '로딩 중...' : '지문 목록 조회'}
            </button>
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 지문 목록 */}
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-800">
                지문 목록 ({passages.length}개)
              </h2>
            </div>
            
            <div className="p-6 max-h-96 overflow-y-auto">
              {passages.length > 0 ? (
                <div className="space-y-4">
                  {passages.map((passage) => (
                    <div
                      key={passage.passageId}
                      className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                        selectedPassage?.passageId === passage.passageId
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => selectPassage(passage)}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-medium text-gray-800 truncate">
                          {passage.title || '제목 없음'}
                        </h3>
                        <span className="text-xs text-gray-500 ml-2">
                          {formatDate(passage.timestamp)}
                        </span>
                      </div>
                      
                      <div className="flex gap-2 mb-2">
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                          {passage.schoolLevel} {passage.grade}학년
                        </span>
                        <span className="px-2 py-1 bg-green-100 text-green-600 text-xs rounded">
                          {passage.passageLength}자
                        </span>
                        {passage.questionsCount > 0 && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-600 text-xs rounded">
                            문항 {passage.questionsCount}개
                          </span>
                        )}
                      </div>
                      
                      <p className="text-sm text-gray-600">
                        {truncateText(passage.passage)}
                      </p>
                      
                      {passage.topic && (
                        <p className="text-xs text-gray-500 mt-1">
                          주제: {passage.topic}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  <p>지문이 없습니다.</p>
                  <p className="text-sm mt-1">먼저 구글 시트 URL을 입력하고 조회해주세요.</p>
                </div>
              )}
            </div>
          </div>

          {/* 선택된 지문 및 문항 생성 */}
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-800">
                문항 생성
              </h2>
            </div>
            
            <div className="p-6">
              {selectedPassage ? (
                <div className="space-y-4">
                  {/* 선택된 지문 정보 */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-medium text-gray-800 mb-2">
                      선택된 지문: {selectedPassage.title}
                    </h3>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>지문 ID: {selectedPassage.passageId}</p>
                      <p>주제: {selectedPassage.topic || '없음'}</p>
                      <p>길이: {selectedPassage.passageLength}자</p>
                      {selectedPassage.vocabularyUsageRate && (
                        <p>어휘 사용률: {selectedPassage.vocabularyUsageRate}%</p>
                      )}
                    </div>
                  </div>

                  {/* 학교급/학년 설정 */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        학교급
                      </label>
                      <select
                        value={questionSettings.schoolLevel}
                        onChange={(e) => setQuestionSettings(prev => ({ ...prev, schoolLevel: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={generatingQuestions}
                      >
                        <option value="">선택하세요</option>
                        <option value="elementary">초등학교</option>
                        <option value="middle">중학교</option>
                        <option value="high">고등학교</option>
                        <option value="advanced">심화과정</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        학년
                      </label>
                      <select
                        value={questionSettings.grade}
                        onChange={(e) => setQuestionSettings(prev => ({ ...prev, grade: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={!questionSettings.schoolLevel || generatingQuestions}
                      >
                        <option value="">선택하세요</option>
                        {questionSettings.schoolLevel === 'elementary' && (
                          <>
                            <option value="3">3학년</option>
                            <option value="4">4학년</option>
                            <option value="5">5학년</option>
                            <option value="6">6학년</option>
                          </>
                        )}
                        {questionSettings.schoolLevel === 'middle' && (
                          <>
                            <option value="1">1학년</option>
                            <option value="2">2학년</option>
                            <option value="3">3학년</option>
                          </>
                        )}
                        {questionSettings.schoolLevel === 'high' && (
                          <>
                            <option value="1">1학년</option>
                            <option value="2">2학년</option>
                            <option value="3">3학년</option>
                          </>
                        )}
                        {questionSettings.schoolLevel === 'advanced' && (
                          <option value="1">심화과정</option>
                        )}
                      </select>
                    </div>
                  </div>

                  {/* 지문 미리보기 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      지문 미리보기
                    </label>
                    <div className="p-3 bg-gray-50 border rounded-md max-h-40 overflow-y-auto">
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">
                        {selectedPassage.passage}
                      </p>
                    </div>
                  </div>

                  {/* 문항 생성 버튼 */}
                  <div className="flex gap-3">
                    <button
                      onClick={generateQuestions}
                      disabled={!questionSettings.schoolLevel || !questionSettings.grade || generatingQuestions}
                      className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {generatingQuestions && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      )}
                      {generatingQuestions ? '문항 생성 중...' : '문항 생성'}
                    </button>
                    
                    {generatingQuestions && (
                      <button
                        onClick={cancelGeneration}
                        className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                      >
                        취소
                      </button>
                    )}
                  </div>

                  {generatingQuestions && (
                    <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-500 mr-2"></div>
                        <span className="font-medium text-green-800">문항을 생성하고 있습니다...</span>
                      </div>
                      <p className="text-sm text-green-700 mt-1">
                        완료되면 HTML 파일이 자동으로 다운로드됩니다.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  <p>지문을 선택해주세요.</p>
                  <p className="text-sm mt-1">왼쪽 목록에서 문항을 생성할 지문을 클릭하세요.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Question Review Editor Modal */}
      {reviewMode && (
        <QuestionReviewEditor
          questions={generatedQuestions}
          onQuestionsUpdate={handleQuestionsUpdate}
          onConfirm={handleReviewConfirm}
          onCancel={handleReviewCancel}
          isLoading={processingHTML}
        />
      )}

      {/* Deployment Modal */}
      <DeploymentModal
        isOpen={showDeploymentModal}
        htmlContent={finalHtmlContent}
        fileName={finalFileName}
        contentType="questions"
        onClose={() => setShowDeploymentModal(false)}
        onDeploymentStart={() => {
          // 배포/다운로드 시작 시 문항 수정 모달 닫기 및 상태 정리
          setReviewMode(false);
          setGeneratedQuestions([]);
          setOriginalQuestions([]);
          setCurrentMetadata(null);
        }}
      />
    </div>
  );
};

export default QuestionGeneratorWindow;